import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { BASE_URL } from "./axios";
import axios from "axios";

const PermissionContext = createContext(null);

const hasAuthSession = () =>
  Boolean(
    sessionStorage.getItem("access_token") &&
      sessionStorage.getItem("customer_id"),
  );

const readCachedRole = () =>
  hasAuthSession() ? sessionStorage.getItem("role") || null : null;

export function persistRole(role) {
  if (role) sessionStorage.setItem("role", role);
}

export function PermissionProvider({ children }) {
  const [userProfile, setProfile] = useState(null);
  const [role, setRole] = useState(readCachedRole);
  const [loading, setLoading] = useState(() => hasAuthSession() && !readCachedRole());

  const fetchUserProfile = useCallback(async () => {
    const token = sessionStorage.getItem("access_token");
    const id = sessionStorage.getItem("customer_id");

    if (!token || !id) {
      setRole(null);
      setProfile(null);
      sessionStorage.removeItem("role");
      setLoading(false);
      return;
    }

    const cached = sessionStorage.getItem("role");
    if (cached) {
      setRole(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const response = await axios.get(
        `${BASE_URL}/user/userviewbyemail/${id}/`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProfile(response.data);
      if (response.data?.role) {
        setRole(response.data.role);
        sessionStorage.setItem("role", response.data.role);
      }
    } catch {
      // Keep the last known role so the menu does not vanish on a slow/failed API.
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePermissions = useCallback(() => {
    const cached = readCachedRole();
    if (cached) setRole(cached);
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    fetchUserProfile();
    const onStorage = (event) => {
      if (!event.key || event.key === "role" || event.key === "access_token") {
        updatePermissions();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchUserProfile, updatePermissions]);

  const value = useMemo(
    () => ({
      role,
      userProfile,
      loading,
      fetchUserProfile,
      updatePermissions,
    }),
    [role, userProfile, loading, fetchUserProfile, updatePermissions],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

const usePermission = () => {
  const ctx = useContext(PermissionContext);
  if (ctx) return ctx;

  return {
    role: readCachedRole(),
    userProfile: null,
    loading: false,
    fetchUserProfile: async () => {},
    updatePermissions: () => {},
  };
};

export default usePermission;
